import { AlertCircle, CheckCircle2, Info, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("rounded-md bg-panel-raised", className)} aria-hidden="true" />;
}

export function LoadingRows({ count = 4 }: { count?: number }) {
  const { t } = useTranslation();
  return (
    <div className="divide-y divide-line" aria-label={t("common.loadingResults", "Loading results")} role="status">
      {Array.from({ length: count }, (_, index) => (
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 px-4 py-4" key={index}>
          <div className="space-y-2">
            <Skeleton className="h-3.5 w-44" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-5 w-20" />
        </div>
      ))}
    </div>
  );
}

export function InlineAlert({
  title,
  message,
  variant = "error",
  onRetry,
}: {
  title: string;
  message: string;
  variant?: "error" | "info" | "success";
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  const Icon = variant === "error" ? AlertCircle : variant === "success" ? CheckCircle2 : Info;
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border px-3.5 py-3 text-sm",
        variant === "error" && "border-danger/35 bg-danger/10 text-danger",
        variant === "info" && "border-info/35 bg-info/10 text-info",
        variant === "success" && "border-success/35 bg-success/10 text-success",
      )}
      role={variant === "error" ? "alert" : "status"}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{title}</p>
        <p className="mt-0.5 text-current/80">{message}</p>
      </div>
      {onRetry && (
        <Button variant="ghost" size="sm" className="-mr-1 shrink-0 text-current" onClick={onRetry}>
          <RefreshCw className="size-3.5" aria-hidden="true" />
          {t("common.retry", "Retry")}
        </Button>
      )}
    </div>
  );
}

export function BusyLabel({ children = "Saving" }: { children?: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
      {children}
    </span>
  );
}

export function EmptyState({ title, message, action }: { title: string; message: string; action?: React.ReactNode }) {
  return (
    <div className="flex min-h-56 flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 flex size-10 items-center justify-center rounded-full bg-panel text-muted">
        <Info className="size-4" aria-hidden="true" />
      </div>
      <p className="font-medium text-ink">{title}</p>
      <p className="mt-1 max-w-sm text-sm leading-6 text-muted">{message}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
